/**
 * Add-Meters dialog (spec 2026-10-02-06).
 *
 * One gesture to equip an asset — and, from a building, floor or site, every
 * lot below it — with one meter per chosen fluid. Backed by the idempotent
 * `POST /assets/{uid}/meters/provision`: a meter of the same type already on
 * a parent is reused, so running it twice never doubles anything.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { mdiLoading } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Label } from '../ui/label'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import { useApi } from '../../api/hooks'
import { refreshEquipmentTab } from '../../hooks/useEquipmentTab'
import { METER_CHOICES, type MeterTargets, type MeterTypeCode } from '../../lib/meters'

interface AddMetersDialogProps {
  org: string
  open: boolean
  onOpenChange: (open: boolean) => void
  assetUid: string
  /** When the asset is itself a lot, the only target is the asset. */
  isLot: boolean
}

export function AddMetersDialog({ org, open, onOpenChange, assetUid, isLot }: AddMetersDialogProps) {
  const { t } = useTranslation('assets')
  const queryClient = useQueryClient()
  const { request } = useApi()
  const [codes, setCodes] = useState<MeterTypeCode[]>([])
  const [targets, setTargets] = useState<MeterTargets>('units')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ created: number; reused: number } | null>(null)

  useEffect(() => {
    if (!open) return
    setCodes(METER_CHOICES.filter(m => m.lotDefault).map(m => m.code))
    setTargets(isLot ? 'self' : 'units')
    setError(null)
    setResult(null)
  }, [open, isLot])

  const toggle = (code: MeterTypeCode, checked: boolean) =>
    setCodes(prev => (checked ? [...prev, code] : prev.filter(c => c !== code)))

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const res = (await request('/api/v1/organizations/{orgCode}/assets/{assetUid}/meters/provision', 'POST', {
        params: { path: { orgCode: org, assetUid } },
        body: { meter_type_codes: codes, targets },
      })) as { created?: unknown[]; reused?: number } | undefined
      setResult({ created: res?.created?.length ?? 0, reused: res?.reused ?? 0 })
      refreshEquipmentTab(queryClient, org, assetUid)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('meters.dialog.error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="add-meters-dialog">
        <DialogHeader>
          <DialogTitle>{t('meters.dialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium mb-1">{t('meters.dialog.types_label')}</legend>
            {METER_CHOICES.map(({ code }) => (
              <div key={code} className="flex items-center gap-2">
                <Checkbox
                  id={`add-meter-${code}`}
                  checked={codes.includes(code)}
                  onCheckedChange={checked => toggle(code, Boolean(checked))}
                  data-testid={`add-meter-${code}`}
                />
                <Label htmlFor={`add-meter-${code}`} className="cursor-pointer font-normal">
                  {t(`meters.types.${code}`)}
                </Label>
              </div>
            ))}
          </fieldset>

          {!isLot && (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium mb-1">{t('meters.dialog.targets_label')}</legend>
              <RadioGroup value={targets} onValueChange={v => setTargets(v as MeterTargets)}>
                {(['units', 'self', 'self_and_units'] as const).map(value => (
                  <div key={value} className="flex items-center gap-2">
                    <RadioGroupItem value={value} id={`add-meters-target-${value}`} data-testid={`add-meters-target-${value}`} />
                    <Label htmlFor={`add-meters-target-${value}`} className="cursor-pointer font-normal">
                      {t(`meters.dialog.targets.${value}`)}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              <p className="text-xs text-muted-foreground">{t('meters.dialog.idempotent_hint')}</p>
            </fieldset>
          )}

          {result && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm" data-testid="add-meters-result">
              {t('meters.dialog.result', { created: result.created, reused: result.reused })}
            </div>
          )}
          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="add-meters-error">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {result ? t('meters.dialog.close') : t('meters.dialog.cancel')}
          </Button>
          {!result && (
            <Button type="button" onClick={handleSubmit} disabled={submitting || codes.length === 0} data-testid="add-meters-submit">
              {submitting && <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" />}
              {t('meters.dialog.submit')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
